import { getJson, postJson } from './client'
import type { CorrectionDetail, CorrectionsPage, CorrectionStatus, CorrectionView, PresetsResponse, SuggestionsPage, TurnsResponse } from '../types/corrections'

// The correction loop: reject or reclassify anything Scout asserted, with a reason that is required.
// The API refuses the call without one — the reason is what the nightly improvement loop learns from —
// so the control never sends an empty one. The presets are server-defined so the web, the model and the
// loop share one vocabulary; the thread is where a person says why when no preset fits.
const corrections = (spoke: string) => `/api/spokes/${encodeURIComponent(spoke)}/scout/corrections`
const suggestions = (spoke: string) => `/api/spokes/${encodeURIComponent(spoke)}/scout/suggestions`

export interface CorrectionRequest {
  entityKind: string
  entityId: string
  fromValue?: string | null
  toValue: string
  reason: string
}

export const correctionsApi = {
  record: (spoke: string, body: CorrectionRequest) => postJson<CorrectionView>(corrections(spoke), body),
  /** Every kind's preset corrections, fetched once and cached: one vocabulary, defined on the server. */
  presets: (spoke: string) => getJson<PresetsResponse>(`${corrections(spoke)}/presets`),
  /** The corrections against one entity: the audit trail behind what the row now says. */
  forEntity: (spoke: string, entityId: string) => getJson<CorrectionsPage>(corrections(spoke), { entityId }),
  /** The person's most recent, across everything — the same block the next scan reads. */
  recent: (spoke: string, take?: number) => getJson<CorrectionsPage>(corrections(spoke), { take }),
  /** The ledger, filtered: what is open, what improved, what the loop could not generalise. */
  list: (spoke: string, filter: { status?: CorrectionStatus; kind?: string; take?: number } = {}) =>
    getJson<CorrectionsPage>(corrections(spoke), { status: filter.status, kind: filter.kind, take: filter.take }),
  /** One correction opened: the investigation, the discussion, the proposals and what was learned. */
  detail: (spoke: string, correctionId: string) => getJson<CorrectionDetail>(`${corrections(spoke)}/${encodeURIComponent(correctionId)}`),
  /** Say more about why it is wrong. The model answers where one is configured; the words are kept either way. */
  addTurn: (spoke: string, correctionId: string, text: string) =>
    postJson<TurnsResponse>(`${corrections(spoke)}/${encodeURIComponent(correctionId)}/turns`, { text }),
  /** What Scout offered rather than asserted (role-aware staging held these at stage none). */
  suggestions: (spoke: string) => getJson<SuggestionsPage>(suggestions(spoke)),
}
